import { GetLegalProtectionRiskResponse } from "api/risk";
import { GenericMessage } from "components/feedback/Message";
import { Sizes } from "components/forms/NumberSelector";
import { NsKey } from "enums/NsKey";
import { useRiskData } from "features/subscription-process/contexts/selectors/selectors";
import { StepPage } from "features/subscription-process/navigation/Steps/StepPage";
import { LEGAL_PROTECTION_ROUTES } from "features/subscription-process/utils/legalProtectionRoutes";
import { useDynamicTranslation } from "hooks/useDynamicTranslation";
import { FC, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { isLegalEntity } from "shared/utils/person";
import { getCompanyStepState } from "./getCompanyStepState";
import { getEligibilityView } from "./getEligibilityView";
import { LegalProtectionCompanySection } from "./LegalProtectionCompanySection";
import { LegalProtectionEligibilityBanner } from "./LegalProtectionEligibilityBanner";
import { LegalProtectionSubsidiariesSection } from "./LegalProtectionSubsidiariesSection";
import { useLegalProtectionCompanyData } from "./useLegalProtectionCompanyData";

/**
 * First step of the legal protection funnel, on a single page: the company, its subsidiaries
 * and its accounting data, each section saving on its own, then the eligibility verdict.
 */
export const LegalProtectionCompanyStepPage: FC = () => {
  const { t } = useDynamicTranslation([NsKey.Common]);
  const { riskId } = useParams<{ riskId: string }>();
  const navigate = useNavigate();
  const riskData = useRiskData() as GetLegalProtectionRiskResponse | null;
  const company = useMemo(() => riskData?.person, [riskData]);
  const legalEntityId = company?.id;
  // SIRET of the company, refused as a subsidiary SIRET. Only a legal entity carries one.
  const parentRegistrationNumber = company && isLegalEntity(company) ? company.registrationNumber : undefined;

  const {
    financials,
    subsidiaries,
    eligibility,
    isLoading,
    subsidiariesError,
    eligibilityError,
    refresh
  } = useLegalProtectionCompanyData(riskId, legalEntityId);

  const view = useMemo(() => (eligibility ? getEligibilityView(eligibility) : null), [eligibility]);
  const { sectionsDisabled, canGoNext } = getCompanyStepState({
    hasCompany: Boolean(company),
    isLoading,
    view
  });

  const stepPageConfig = useMemo(() => ({
    nextConfig: {
      label: t("COMMON.NEXT_STEP"),
      disabled: !canGoNext,
      onClick: () => navigate(`${LEGAL_PROTECTION_ROUTES.pricing}/${riskId}`),
    },
    containerSize: Sizes["5xl"],
  }), [t, canGoNext, navigate, riskId]);

  return (
    <StepPage {...stepPageConfig}>
      <div className="flex flex-col gap-6">
        {/* What the contract can cover, told before the company and its subsidiaries are entered. */}
        <GenericMessage
          type="info"
          dismissible={false}
          message={t("SUBSCRIPTION_PROCESS.LEGAL_PROTECTION.GROUP_COVERAGE_NOTICE")}
        />
        {riskId && (
          <LegalProtectionCompanySection company={company} riskId={riskId} financials={financials} onSaved={refresh} />
        )}
        {company && subsidiariesError && (
          <GenericMessage
            type="error"
            message={t("SUBSCRIPTION_PROCESS.LEGAL_PROTECTION.SUBSIDIARIES_LOAD_ERROR")}
            dismissible={false}
            actionButton={{
              label: t("SUBSCRIPTION_PROCESS.LEGAL_PROTECTION.RETRY"),
              onClick: refresh,
            }}
          />
        )}
        <LegalProtectionSubsidiariesSection
          legalEntityId={legalEntityId}
          parentRegistrationNumber={parentRegistrationNumber}
          subsidiaries={subsidiaries}
          disabled={sectionsDisabled}
          onSaved={refresh}
        />
        {/* Without a company the banner stays neutral: there is no verdict to show yet. */}
        <LegalProtectionEligibilityBanner view={view} hasError={eligibilityError} onRetry={refresh} />
      </div>
    </StepPage>
  );
};
